import { useEffect, useState, useMemo } from "react";
import { useAppSelector } from "../store/hooks";

/**
 * Custom hook to manage the state of tabs in the Service Agreement.
 * It integrates various aspects of the service agreement, such as customer validation,
 * plan validation, billing validation, and active tab management.
 *
 * @returns An object containing:
 * - `isPaymentEnabled`: Boolean indicating whether the payment tab should be enabled.
 * - `serviceAgreementActiveTab`: The currently active tab in the Service Agreement UI.
 * - `serviceAgreementTabsList`: List of tabs available in the Service Agreement UI.
 */
export const useCarePlanTabs = () => {
    // Selectors to fetch state from the Redux store.
    const serviceAgreementActiveTab = useAppSelector(({ ui }) => ui.CarePlanActiveTab);
    const serviceAgreementTabsList = useAppSelector(({ ui }) => ui.CarePlanTabsList);
    const serviceAgreementPolicyPlanType = useAppSelector(({ ui }) => ui.CarePlanPlanDetailsPlanOptionsPlanType);
    const isPaymentValid = useAppSelector(({ state }) => state.CarePlanPaymentValid);
    const isBillingValid = useAppSelector(({ state }) => state.CarePlanBillingValid);
    const isCustomerValid = useAppSelector(({ state }) => state.CarePlanCustomerValid);
    const isPlanDetailsValid = useAppSelector(({ state }) => state.CarePlanPlanDetailsValid);

    const [isPaymentEnabled, setIsPaymentEnabled] = useState(false);

    // Memoize the condition for enabling payment to optimize performance.
    const shouldEnablePayment = useMemo(() => {
        return isPlanDetailsValid && isCustomerValid && isBillingValid && !!serviceAgreementPolicyPlanType?.feeAnnual && serviceAgreementPolicyPlanType.feeAnnual > 0
    }, [isCustomerValid, isPlanDetailsValid, isBillingValid, serviceAgreementPolicyPlanType]);

    // Effect to update the state of payment enabled based on the memoized condition.
    useEffect(() => {
        setIsPaymentEnabled(shouldEnablePayment);
    }, [shouldEnablePayment]);

    return { isPaymentEnabled, serviceAgreementActiveTab, serviceAgreementTabsList, isPlanDetailsValid, isCustomerValid, isBillingValid, isPaymentValid };
}
